import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FileDataModel, estateTypes, projectClassifications, subjectPropertyTypes } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import { occupancyTypes } from '../../../utils/shared-property-info.lookup';
import { Cmg1003LoanQuery } from 'apps/cmg-clear2-ui/src/app/loan-state/loan/loan.query';
import { Cmg1003FileDataQuery, Cmg1003PropertyQuery, Cmg1003StoreQueryService } from 'apps/cmg-clear2-ui/src/app/loan-state';
import { Loan1008Model } from 'apps/cmg-clear2-ui/src/app/loan-1008/models/loan-1008.model';
import { Subject, combineLatest, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { Loan1008Service } from 'apps/cmg-clear2-ui/src/app/loan-1008/loan-1008.service';
import { SessionQuery } from 'apps/cmg-clear2-ui/src/app/core/state';
import { Panel } from 'primeng/panel';
import { PrimeTemplate } from 'primeng/api';
import { AsyncPipe, CurrencyPipe } from '@angular/common';
import { EmptyToDashPipe } from '../../../pipes/empty-to-dash.pipe';
import { FormatSocialSecurityPipe } from '../../../pipes/social-security-number.pipe';
import { ToNameOrValueNoSelectPipe } from '../../../pipes/to-name-no-select.pipe';
import { InfoPanelContentComponent } from '../components/info-panel-content/info-panel-content.component';
import { InfoRowComponent } from '../components/info-row/info-row.component';

@Component({
  selector: 'cmg-borrower-property-information',
  templateUrl: './borrower-property-information.component.html',
  styleUrls: ['./borrower-property-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Panel,
    PrimeTemplate,
    AsyncPipe,
    CurrencyPipe,
    EmptyToDashPipe,
    FormatSocialSecurityPipe,
    ToNameOrValueNoSelectPipe,
    InfoPanelContentComponent,
    InfoRowComponent,
  ],
})
export class BorrowerPropertyInformationComponent implements OnInit, OnDestroy {
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _storeQueryService = inject(Cmg1003StoreQueryService);
  private readonly _propertyQuery = inject(Cmg1003PropertyQuery);
  private readonly _loan1008Service = inject(Loan1008Service);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _sessionQuery = inject(SessionQuery);

  readonly fileDataId = input<number>(undefined);

  fileData: FileDataModel;
  loan1008Data: Loan1008Model;
  projectClassifications = projectClassifications;
  occupancyTypes = occupancyTypes;
  subjectPropertyTypes = subjectPropertyTypes;
  estateTypes = estateTypes;

  loanInfo$ = this._loanQuery.selectLoanInfo();
  propertyData$ = this._propertyQuery.select();

  primaryBorrower$ = this._storeQueryService
    .getBorrowersBySelectedApplicationId()
    .pipe(map((borrowers) => borrowers.find((borrower) => borrower.isPrimaryBorrower)));
  additionalBorrower$ = this._storeQueryService
    .getBorrowersBySelectedApplicationId()
    .pipe(map((borrowers) => borrowers.find((borrower) => !borrower.isPrimaryBorrower)));

  private _destroyed$ = new Subject();

  ngOnInit(): void {
    if (this.fileDataId() > 0) {
      combineLatest({
        fileData: this._sessionQuery
          .select((state) => state.all1003Loaded)
          .pipe(
            filter((loaded) => loaded),
            takeUntil(this._destroyed$),
            switchMap(() => this._fileDataQuery.select()),
          ),
        loan1008Data: this._loan1008Service.getLoan1008data(this.fileDataId()),
      })
        .pipe(take(1))
        .subscribe(({ fileData, loan1008Data }) => {
          this.fileData = fileData;
          this.loan1008Data = loan1008Data;
          this._cdRef.detectChanges();
        });
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  propertyFullAddress(street, city, state, zip) {
    const addressParts = [];
    if (street) {
      addressParts.push(street);
    }
    if (city) {
      addressParts.push(city);
    }
    if (state) {
      addressParts.push(state);
    }
    if (zip) {
      addressParts.push(zip);
    }
    return addressParts.length > 0 ? addressParts.join(', ') : '-';
  }
}
